// Activité d'un site de l'écran « Google Sites » — dérivée du DERNIER COMMENTAIRE RÉEL du site
// (`hosting_detected.last_comment_at`, google-sites-install B4_02), jamais de « Dernière cassée » :
// celle-ci mesure des chargements du widget (visiteurs et robots), pas une communauté vivante —
// un site abandonné dont le widget est encore embarqué y paraît vivant.
//
// ⚠ Module DISTINCT de `hostingFreshness.js` (fraîcheur d'un constat ≠ activité d'un site) et de
// `websiteActivity.js` (liste Communautés : « widget chargé dans les 30 j »). Le libellé
// « Actif / Inactif » est le même que sur la liste Communautés, avec un autre critère — décision
// Mathias D82 ; c'est la colonne « Dernier commentaire » qui dit lequel.
//
// Le SEUIL n'est jamais recopié ici : il arrive avec la réponse de l'API (`inactive_after_days`),
// pour que le classement de l'écran et le filtre du serveur ne puissent pas diverger.
//
// Quatre états, jamais deux (gabarit `websiteActivity.js`) :
//   - `unmeasured` : champ ABSENT — marqueur pas encore re-mesuré, ou API d'avant B4_02. Ce n'est
//     NI actif NI inactif (le serveur ne le sort dans aucun des deux filtres) ;
//   - `inactive` + `never` : `null` — mesuré, le site n'a jamais reçu de commentaire ;
//   - `active` / `inactive` : date, classée avec le seuil reçu (borne incluse côté actif, comme
//     `buildActivityFilter` côté API) ;
//   - `unknown` : date présente mais seuil non servi — on montre la date, on ne classe pas.
// Fonctions pures, testées dans `src/tests/googleSitesActivity.test.js` (D38).

const DAY_MS = 24 * 60 * 60 * 1000;

export const ACTIVITY_MODES = ['all', 'active', 'inactive'];

// Le sélecteur n'est utilisable que si l'API sert le seuil : sans lui, elle ignore `activity` et
// rendrait une liste NON filtrée sous un sélecteur qui affiche « Inactifs ».
export const isActivityFilterAvailable = (inactiveAfterDays) =>
  typeof inactiveAfterDays === 'number' && Number.isFinite(inactiveAfterDays) && inactiveAfterDays > 0;

// Ancienneté en clair. Mois de 30 j, années de 365 j : une indication, pas un calendrier.
export const agoLabel = (days) => {
  if (!Number.isFinite(days) || days < 0) return '';
  if (days === 0) return 'aujourd’hui';
  if (days < 60) return `il y a ${days} j`;
  if (days < 730) return `il y a ${Math.floor(days / 30)} mois`;
  return `il y a ${Math.floor(days / 365)} ans`;
};

export function googleSitesActivity(lastCommentAt, inactiveAfterDays, now = new Date()) {
  if (lastCommentAt === undefined) {
    return { state: 'unmeasured', never: false, days: null, label: null, ago: null };
  }
  if (lastCommentAt === null) {
    return { state: 'inactive', never: true, days: null, label: 'Inactif', ago: 'jamais' };
  }
  const t = new Date(lastCommentAt).getTime();
  if (!Number.isFinite(t)) {
    return { state: 'unmeasured', never: false, days: null, label: null, ago: null };
  }
  const elapsed = now.getTime() - t;
  const days = Math.max(0, Math.floor(elapsed / DAY_MS));
  const ago = agoLabel(days);
  if (!isActivityFilterAvailable(inactiveAfterDays)) {
    return { state: 'unknown', never: false, days, label: null, ago };
  }
  // Même borne que l'API : actif si le commentaire est AU seuil ou après (`$gte`).
  const active = elapsed <= inactiveAfterDays * DAY_MS;
  return {
    state: active ? 'active' : 'inactive',
    never: false,
    days,
    label: active ? 'Actif' : 'Inactif',
    ago
  };
}

// Grisage d'une ligne du tableau (décision Mathias 2026-09-20, amende D67). Le grisage dit « ce
// site ne vit plus » : il suit donc l'ACTIVITÉ, pas l'âge de la dernière cassée — sinon un site
// commenté la semaine dernière mais dont la dernière page cassée date de 2023 apparaît estompé,
// et un zombie (widget chargé hier, aucun commentaire depuis deux ans) en pleine opacité.
//   - actif                → jamais grisé ;
//   - inactif              → grisé ;
//   - non mesuré / inconnu → on ne sait pas : repli sur l'ancien critère (dernière cassée ancienne).
export const isRowDimmed = (activityState, oldBreak) => {
  if (activityState === 'active') return false;
  if (activityState === 'inactive') return true;
  return Boolean(oldBreak);
};

// Libellé du compteur d'en-tête : le total servi est celui du filtre courant — « Sites concernés »
// mentirait dès qu'un filtre est posé.
export const totalLabel = (activity) => {
  if (activity === 'active') return 'Sites actifs';
  if (activity === 'inactive') return 'Sites inactifs';
  return 'Sites concernés';
};

// ── Suppression intégrale d'un site depuis la liste (B4_02 objectif 3) ───────────────────────────

// Suppression ARRÊTÉE à la main ou INTERROMPUE par une erreur : le site est déjà à la corbeille
// (le moteur l'y passe dès le LANCEMENT) mais garde ses fils — l'API le sert quand même, avec
// `purge.status` (D90). Libellés alignés sur la fenêtre de suivi (« arrêtée par toi » /
// « interrompue par une erreur ») ; ⛔ jamais « en pause », qui y désigne la suspension
// automatique, celle qui reprend seule. `undefined` (API d'avant, ou aucune suppression) → null.
const PURGE_HALT_LABELS = { stopped: 'purge arrêtée', interrupted: 'purge interrompue' };
export const purgeHalt = (purge) => {
  const status = purge && purge.status;
  // `hasOwnProperty` : un état `toString` / `constructor` résoudrait sur le prototype de l'objet.
  return Object.prototype.hasOwnProperty.call(PURGE_HALT_LABELS, status)
    ? { state: status, label: PURGE_HALT_LABELS[status] }
    : null;
};

// Sur quelles lignes le bouton de suppression apparaît (décision Mathias 2026-09-21, 🛑7) : les
// sites INACTIFS seulement — la page sert à les trouver, et un site vivant ne peut pas être
// supprimé d'ici par mégarde (sa fiche garde le bouton). « Non mesuré » / « inconnu » : on ne sait
// pas → pas de bouton. Une suppression arrêtée se reprend d'ici, quel que soit le classement.
export const canPurgeFromRow = (activityState, purge) =>
  activityState === 'inactive' || purgeHalt(purge) !== null;

// Page à afficher quand le total a baissé (un site supprimé sort de la liste servie) : jamais
// au-delà de la dernière page, jamais 0 — sinon « Page 12 / 11 » et un tableau vide.
export const clampPage = (page, total, pageSize) => {
  const size = Number(pageSize) > 0 ? Number(pageSize) : 1;
  const last = Math.max(1, Math.ceil((Number(total) || 0) / size));
  const wanted = Number.isInteger(page) && page > 0 ? page : 1;
  return Math.min(wanted, last);
};
